import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Link, withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";

const SidebarContent = props => {
  const currentPath = props.location.pathname;

  // Target Domain State
  const [targetDomain, setTargetDomain] = useState(() => {
    return localStorage.getItem("domain") || "";
  });

  // User Profile State
  const [userName, setUserName] = useState(() => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        return parsed.FirstName || parsed.username || parsed.displayName || "Sakshi";
      } catch (e) {}
    }
    return "Sakshi";
  });

  // Real exposure counts state (fallback to null until loaded)
  const [counts, setCounts] = useState(() => {
    try {
      const cached = localStorage.getItem("exposureCounts");
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return {
      credentials: 0,
      subdomains: 0,
      whois: 0,
      dns: 0,
      ports: 0,
      reverseDns: 0,
      reverseIp: 0,
    };
  });

  const userID = localStorage.getItem("userID");
  const orgID = localStorage.getItem("orgID");
  const userCheck = localStorage.getItem("userID");

  const loadData = useCallback(async () => {
    if (!orgID) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}riskcalculator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userID,
          orgID,
          exposedCredentials: 165112129704651,
          exposedSubdomains: 165112129704687,
          exposedWHOIS: 165112129704697,
          exposedDNS: 165112129704677,
          exposedPorts: 165112129704796,
          exposedReverseDNS: 165112129704189,
          exposedReverseIP: 165112129704642,
        }),
      });
      const resData = await response.json();
      if (resData && resData.data) {
        const d = resData.data;

        // 1. Resolve Target Domain
        const PUBLIC_PROVIDERS = [
          "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "live.com",
          "aol.com", "icloud.com", "mail.com", "protonmail.com", "zoho.com",
          "yandex.com", "gmx.com", "fastmail.com", "tutanota.com", "example.com"
        ];
        let foundDomain = "";
        const domains = d.orgAsset?.Domain || [];
        const assets = d.orgAsset?.Asset || [];
        for (const item of domains) {
          const val = item.Domain || item.Value;
          if (val && !PUBLIC_PROVIDERS.includes(val.toLowerCase().trim())) {
            foundDomain = val.trim();
            break;
          }
        }
        if (!foundDomain) {
          for (const item of assets) {
            if (item.Type === "Domain" && item.Value && !PUBLIC_PROVIDERS.includes(item.Value.toLowerCase().trim())) {
              foundDomain = item.Value.trim();
              break;
            }
          }
        }
        if (!foundDomain && domains.length > 0) {
          const cand = domains[0]?.Domain || domains[0]?.Value;
          if (cand && !PUBLIC_PROVIDERS.includes(cand.toLowerCase().trim())) {
            foundDomain = cand.trim();
          }
        }
        if (foundDomain) {
          setTargetDomain(foundDomain);
          localStorage.setItem("domain", foundDomain);
        }

        // 2. Count DNS items accurately
        let dnsTotal = 0;
        if (d.exposedDNS?.Data && Array.isArray(d.exposedDNS.Data)) {
          d.exposedDNS.Data.forEach(item => {
            ['A', 'AAAA', 'CNAME', 'SOA', 'MX', 'NS', 'TXT', 'CAA'].forEach(recType => {
              if (Array.isArray(item[recType])) {
                dnsTotal += item[recType].length;
              }
            });
          });
          if (dnsTotal === 0) dnsTotal = d.exposedDNS.Data.length;
        }

        // 3. Count Ports items
        let portsTotal = 0;
        if (d.exposedPorts?.Data && Array.isArray(d.exposedPorts.Data)) {
          d.exposedPorts.Data.forEach(item => {
            if (Array.isArray(item.Ports)) {
              portsTotal += item.Ports.length;
            } else {
              portsTotal += 1;
            }
          });
        }

        // 4. Count Reverse IP items
        let ripTotal = 0;
        if (d.exposedReverseIP?.Data && Array.isArray(d.exposedReverseIP.Data)) {
          d.exposedReverseIP.Data.forEach(item => {
            if (Array.isArray(item.Domains)) {
              ripTotal += item.Domains.length;
            } else {
              ripTotal += 1;
            }
          });
        }

        const calculatedCounts = {
          credentials: Array.isArray(d.exposedCredentials?.Data) ? d.exposedCredentials.Data.length : 0,
          subdomains: Array.isArray(d.exposedSubdomains?.Data) ? d.exposedSubdomains.Data.length : 0,
          whois: Array.isArray(d.exposedWHOIS?.Data) ? d.exposedWHOIS.Data.length : 0,
          dns: dnsTotal,
          ports: portsTotal,
          reverseDns: Array.isArray(d.exposedReverseDNS?.Data) ? d.exposedReverseDNS.Data.length : 0,
          reverseIp: ripTotal,
        };

        setCounts(calculatedCounts);
        localStorage.setItem("exposureCounts", JSON.stringify(calculatedCounts));
      }
    } catch (err) {
      console.warn("Sidebar risk data fetch error:", err);
    }
  }, [userID, orgID]);

  useEffect(() => {
    // Initial fetch if domain or counts missing
    loadData();

    // Check user profile name
    async function fetchUser() {
      if (!userCheck) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}user-profile`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userCheck }),
        });
        const data = await response.json();
        if (data && data.user && data.user.FirstName) {
          setUserName(data.user.FirstName);
        }
      } catch (err) {}
    }
    fetchUser();

    // Event listeners to sync data reactively across dashboard / page scans
    const handleDomainUpdate = () => {
      const d = localStorage.getItem("domain");
      if (d) setTargetDomain(d);
    };

    const handleCountsUpdate = () => {
      try {
        const c = localStorage.getItem("exposureCounts");
        if (c) setCounts(JSON.parse(c));
      } catch (e) {}
    };

    window.addEventListener("domainChanged", handleDomainUpdate);
    window.addEventListener("exposureCountsChanged", handleCountsUpdate);

    return () => {
      window.removeEventListener("domainChanged", handleDomainUpdate);
      window.removeEventListener("exposureCountsChanged", handleCountsUpdate);
    };
  }, [loadData, userCheck]);

  // Clean formatting of scanned domain name
  const displayDomain = targetDomain
    ? (targetDomain.startsWith("www.") ? targetDomain : `www.${targetDomain}`)
    : "Loading...";

  const initial = (userName || "S").charAt(0).toUpperCase();

  // Exposure items with real dynamic mapped counts
  const exposureItems = [
    {
      label: props.t("Exposed Credentials"),
      path: "/exposedcredentials",
      count: counts.credentials,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="7.5" cy="15.5" r="4.5" />
          <path d="M10.5 12.5L20 3" />
          <path d="M16 3l3 3" />
          <path d="M13 6l3 3" />
        </svg>
      ),
    },
    {
      label: props.t("Exposed Subdomains"),
      path: "/exposedsubdomains",
      count: counts.subdomains,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="18" r="3" />
          <circle cx="6" cy="6" r="3" />
          <circle cx="18" cy="6" r="3" />
          <path d="M6 9v12" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        </svg>
      ),
    },
    {
      label: props.t("Exposed WHOIS"),
      path: "/exposedwhois",
      count: counts.whois,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      ),
    },
    {
      label: props.t("Exposed DNS"),
      path: "/exposeddns",
      count: counts.dns,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
    {
      label: props.t("Exposed PORTS"),
      path: "/exposedports",
      count: counts.ports,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
          <line x1="6" y1="6" x2="6.01" y2="6" />
          <line x1="6" y1="18" x2="6.01" y2="18" />
        </svg>
      ),
    },
    {
      label: props.t("Exposed Reverse DNS"),
      path: "/exposedreversedns",
      count: counts.reverseDns,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 3 21 3 21 8" />
          <line x1="4" y1="20" x2="21" y2="3" />
          <polyline points="21 16 21 21 16 21" />
          <line x1="15" y1="15" x2="21" y2="21" />
          <line x1="4" y1="4" x2="9" y2="9" />
        </svg>
      ),
    },
    {
      label: props.t("Exposed Reverse IP"),
      path: "/exposedreverseip",
      count: counts.reverseIp,
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      ),
    },
    {
      label: props.t("Brand Protection"),
      path: "/brand-protection",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <circle cx="12" cy="11" r="3" />
        </svg>
      ),
    },
  ];

  // Testing menu items
  const testingItems = [
    {
      label: props.t("Black Box Findings"),
      path: "/exposedblackbox",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      label: props.t("Grey Box Testing"),
      path: "/greyboxtesting",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      ),
    },
  ];

  // My Account menu items
  const accountItems = [
    {
      label: props.t("My Profile"),
      path: "/myprofile",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      label: props.t("Asset Inventory"),
      path: "/assetinventory",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      ),
    },
    {
      label: props.t("Scan Settings"),
      path: "/scansettings",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      ),
    },
  ];

  return (
    <div className="asm-sidebar-wrapper">
      {/* TARGET Section */}
      <div className="asm-sidebar-target-wrap">
        <div className="asm-sidebar-section-title">{props.t("TARGET")}</div>
        <div className="asm-target-pill">
          <span className="asm-target-dot" />
          <span className="asm-target-domain" title={displayDomain}>
            {displayDomain}
          </span>
        </div>
      </div>

      {/* Single Clean Scroll Container (No duplicate simplebar scroll tracks) */}
      <div className="asm-sidebar-scroll">
        <nav className="asm-sidebar-nav">
          {/* OVERVIEW */}
          <div className="asm-sidebar-section-title">{props.t("OVERVIEW")}</div>
          <Link
            to="/dashboard"
            className={`asm-nav-item ${
              currentPath === "/dashboard" || currentPath === "/" ? "active" : ""
            }`}
          >
            <div className="asm-nav-left">
              <span className="asm-nav-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </span>
              <span className="asm-nav-label">{props.t("Risk Dashboard")}</span>
            </div>
          </Link>

          <Link
            to="/findings"
            className={`asm-nav-item ${currentPath === "/findings" ? "active" : ""}`}
          >
            <div className="asm-nav-left">
              <span className="asm-nav-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </span>
              <span className="asm-nav-label">{props.t("Findings")}</span>
            </div>
          </Link>

          {/* EXPOSURE */}
          <div className="asm-sidebar-section-title">{props.t("EXPOSURE")}</div>
          {exposureItems.map(item => {
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`asm-nav-item ${isActive ? "active" : ""}`}
              >
                <div className="asm-nav-left">
                  <span className="asm-nav-icon">{item.icon}</span>
                  <span className="asm-nav-label">{item.label}</span>
                </div>
                {item.count !== null && item.count !== undefined && (
                  <span className="asm-nav-count">{item.count}</span>
                )}
              </Link>
            );
          })}

          {/* TESTING */}
          <div className="asm-sidebar-section-title">{props.t("TESTING")}</div>
          {testingItems.map(item => {
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`asm-nav-item ${isActive ? "active" : ""}`}
              >
                <div className="asm-nav-left">
                  <span className="asm-nav-icon">{item.icon}</span>
                  <span className="asm-nav-label">{item.label}</span>
                </div>
              </Link>
            );
          })}

          {/* MY ACCOUNT */}
          <div className="asm-sidebar-section-title">{props.t("MY ACCOUNT")}</div>
          {accountItems.map(item => {
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`asm-nav-item ${isActive ? "active" : ""}`}
              >
                <div className="asm-nav-left">
                  <span className="asm-nav-icon">{item.icon}</span>
                  <span className="asm-nav-label">{item.label}</span>
                </div>
              </Link>
            );
          })}
          {(["tenant_admin", "auditor", "super_admin"].includes(String(localStorage.getItem("role") || "").toLowerCase())) && (
            <Link
              to="/audit-logs"
              className={`asm-nav-item ${currentPath === "/audit-logs" ? "active" : ""}`}
            >
              <div className="asm-nav-left">
                <span className="asm-nav-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </span>
                <span className="asm-nav-label">{props.t("Audit Logs")}</span>
              </div>
            </Link>
          )}
          {(localStorage.getItem("role") === "tenant_admin" || localStorage.getItem("role") === "super_admin") && (
            <Link
              to="/users"
              className={`asm-nav-item ${currentPath === "/users" ? "active" : ""}`}
            >
              <div className="asm-nav-left">
                <span className="asm-nav-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </span>
                <span className="asm-nav-label">{props.t("User Management")}</span>
              </div>
            </Link>
          )}
        </nav>
      </div>

      {/* Bottom Pinned User Profile Card */}
      <div className="asm-sidebar-footer">
        <Link to="/myprofile" className="asm-sidebar-user-card">
          <div className="d-flex align-items-center">
            <div className="asm-user-avatar">{initial}</div>
            <div className="asm-user-meta">
              <div className="asm-user-name">{userName}</div>
              <div className="asm-user-sub">{props.t("My account")}</div>
            </div>
          </div>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="asm-user-chevron"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
  type: PropTypes.string,
};

export default withRouter(withTranslation()(SidebarContent));
